import cx from 'classnames';
import {ceil, filter, find, map, max, min} from 'lodash';
import {useCallback, useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import useResizeObserver from 'use-resize-observer';

import {Select, Icon} from 'components';
import {Option} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';

import {PageItem} from './PageItem';
import {ITEM_WIDTH, MAX_ITEMS, PAGE_SIZES} from './constants';

const T_KEYS = TRANSLATION_KEYS.UI_KIT.PAGINATION;

type Props = {
    pageNumber: number;
    pageSize: number;
    pageSizes?: number[];
    hidePageSizes?: boolean;
    totalItems?: number;
    hideNext?: boolean;
    hidePrev?: boolean;
    className?: string;
    itemsClassName?: string;
    rangeClassName?: string;
    pageSelectorClassName?: string;
    maxItemsToDisplay?: number;
    onPageNumberChange: (pageNumber: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
};

export const Pagination: React.FC<Props> = ({
    hidePrev, hideNext, pageNumber, className, onPageNumberChange, onPageSizeChange, rangeClassName, pageSelectorClassName,
    hidePageSizes, pageSize, totalItems, pageSizes = PAGE_SIZES, maxItemsToDisplay: maxItemsToDisplayProp, itemsClassName
}) => {
    const {t} = useTranslation();
    const pageCount = useMemo(() => ceil(totalItems / pageSize), [totalItems, pageSize]);

    const itemsRef = useRef<HTMLDivElement>(null);
    const contentRefRect = useResizeObserver<HTMLDivElement>({ref: itemsRef});

    const maxItemsByWidth = useMemo(() => {
        let canHaveItems = contentRefRect?.width ? Math.floor(contentRefRect?.width / ITEM_WIDTH) : null;
        if (!canHaveItems) {
            return null;
        }
        if (!hideNext) {
            canHaveItems--;
        }
        if (!hidePrev) {
            canHaveItems--;
        }
        if (canHaveItems % 2 === 0) {
            canHaveItems--;
        }
        return canHaveItems;
    }, [contentRefRect, hideNext, hidePrev]);

    const maxItemsToDisplay = useMemo(() => {
        let result = maxItemsToDisplayProp;
        if (maxItemsToDisplayProp < 1) {
            result = MAX_ITEMS;
        } else if (maxItemsToDisplayProp === 1) {
            result = 1;
        } else if (maxItemsToDisplayProp % 2 === 0) {
            result = maxItemsToDisplayProp - 1;
        }
        return min([result, maxItemsByWidth]);
    }, [maxItemsToDisplayProp, maxItemsByWidth]);
    const firstPage = useMemo(() => {
        let result;
        if (pageNumber <= (maxItemsToDisplay + 1) / 2) {
            result = 1;
        } else if (pageNumber + (maxItemsToDisplay + 1) / 2 > pageCount) {
            result = pageCount - maxItemsToDisplay + 1;
        } else {
            result = pageNumber - (maxItemsToDisplay + 1) / 2 + 1;
        }
        return max([1, result]);
    }, [pageNumber, pageCount, maxItemsToDisplay]);

    const visiblePagesCount = useMemo(() => min([maxItemsToDisplay, pageCount]), [pageCount, maxItemsToDisplay]);

    const pageItems = useMemo(() => {
        const indexes = Array.from(Array(visiblePagesCount).keys());
        const filteredIndexes = filter(indexes, (index) => !(index === 0 || index === visiblePagesCount - 1));

        return map(filteredIndexes, (index) => ({
            page: index + firstPage,
            disabled: false,
            index
        }));
    }, [firstPage, visiblePagesCount]);

    const range = useMemo(() => {
        const from = (pageNumber - 1) * pageSize;
        const itemsOnPage = min([totalItems - from, pageSize]);
        const to = from + itemsOnPage;
        return (
            <div className='tw-flex'>
                <span>{`${from + 1} - ${to}`}</span>
                <span className='tw-mx-1'>{t(T_KEYS.OF)}</span>
                <span>{totalItems}</span>
            </div>
        );
    }, [pageNumber, pageSize, totalItems, t]);

    const pageSizeOptions = useMemo(() => map(pageSizes, (pSize: number) => ({
        id: String(pSize),
        name: `${pSize}/${t(T_KEYS.PAGE)}`
    } as Option)), [pageSizes, t]);

    const initialPageSizeOption: Option = useMemo(() =>
        find<Option>(pageSizeOptions, {id: String(pageSize)}), [pageSizeOptions, pageSize]
    );

    const handlePageSizeChange = useCallback(({id}: Option) => {
        onPageSizeChange(+id);
        onPageNumberChange(1);
    }, [onPageSizeChange, onPageNumberChange]);

    return (
        <div className={cx(className, 'tw-flex tw-items-center tw-justify-end tw-my-2')}>
            <div ref={itemsRef} className={cx('tw-flex tw-items-center tw-flex-1', itemsClassName)}>
                {!hidePrev &&
                    <PageItem
                        pageNumber={pageNumber - 1}
                        disabled={pageNumber === 1}
                        onSelect={onPageNumberChange}
                    >
                        <Icon name='chevron-left' size='sm' />
                    </PageItem>}
                <PageItem
                    pageNumber={1}
                    selected={pageNumber === 1}
                    disabled={false}
                    onSelect={onPageNumberChange}
                >
                    {1}
                </PageItem>
                {map(pageItems, (item, index) => {
                    const ellipsisFirst = maxItemsToDisplay < pageCount && index === 0 && firstPage >= 2;
                    const ellipsisLast = maxItemsToDisplay < pageCount && index === maxItemsToDisplay - 3 &&
                    pageCount - firstPage >= maxItemsToDisplay;
                    return (
                        <PageItem
                            key={index}
                            pageNumber={item.page}
                            selected={item.page === pageNumber}
                            disabled={item.disabled}
                            ellipsis={ellipsisFirst || ellipsisLast}
                            onSelect={onPageNumberChange}
                        >
                            {item.page}
                        </PageItem>
                    );
                })}
                {pageCount > 1 &&
                    <PageItem
                        pageNumber={pageCount}
                        selected={pageNumber === pageCount}
                        disabled={false}
                        onSelect={onPageNumberChange}
                    >
                        {pageCount}
                    </PageItem>}
                {!hideNext &&
                    <PageItem
                        pageNumber={pageNumber + 1}
                        disabled={pageNumber === pageCount}
                        onSelect={onPageNumberChange}
                    >
                        <Icon name='chevron-right' size='sm' />
                    </PageItem>}
            </div>

            <div className={cx('tw-mx-4 tw-whitespace-nowrap', rangeClassName)}>
                {range}
            </div>

            {!hidePageSizes &&
                <Select
                    value={initialPageSizeOption}
                    options={pageSizeOptions}
                    hidePlaceholder
                    className={cx('tw-w-30', pageSelectorClassName)}
                    onChange={handlePageSizeChange}
                />}
        </div>
    );
};